// ── bulk-import-result: interpret the AUTHORITATIVE backend bulk response ────
// Wave 5. After the confirmed POST, the page renders the backend's OWN outcome —
// never the client preview. Two real shapes (see bulk-import-parse.ts header):
//
//  LIVE  (toLiveOperationResult): all-or-nothing. On full success the envelope
//        maps to { ok:true, status?, reference?, id? } with NO per-row data and
//        NO imported_count (the backend returns {participants, count}; the live
//        readmodel keeps ok/id/status/reference). A failure THROWS a
//        CO_VALIDATION_FAILED — it never resolves with a partial result, so the
//        page's catch renders the error; this module only sees resolved results.
//
//  MOCK  (coaching-mock-cohort-lifecycle): rich — imported_count, failed_count,
//        row_results[], and status "imported" | "partial_failure" | "dry_run".
//
// We normalize BOTH into one view the page renders, degrading gracefully when the
// per-row fields are absent (live). No fabrication: when the backend gives only a
// count we show the count; we never synthesize per-row outcomes the server didn't
// return.

import type { CoachingOperationResult } from "../../../services/coaching-dtos";

export interface BulkResultRow {
  row: number;
  email: string;
  status: string;
  /** "ok" rows succeeded; anything else is a failure/skip the server reported. */
  ok: boolean;
}

export interface BulkImportResultView {
  /** True when the import committed at least one participant with no failures. */
  succeeded: boolean;
  /** True when SOME rows imported and SOME failed (mock partial_failure). */
  partial: boolean;
  /** True when the server reported a validation failure (no rows committed). */
  validationFailed: boolean;
  importedCount: number;
  failedCount: number;
  /** Per-row outcomes when the backend provided them (mock); else empty (live). */
  rowResults: BulkResultRow[];
  /** A short, honest summary line for the toast/alert. */
  summary: string;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function rowOk(status: string): boolean {
  return status === "ready" || status === "imported" || status === "ok" || status === "created";
}

/**
 * Normalize a resolved bulk operation result. `submittedReadyCount` is how many
 * rows the client sent (the ready rows) — used as the imported-count fallback for
 * the LIVE all-or-nothing success shape, which reports ok:true but no count.
 */
export function describeBulkImportResult(
  result: CoachingOperationResult,
  submittedReadyCount: number,
): BulkImportResultView {
  const rawRows = Array.isArray(result.row_results) ? result.row_results : [];
  const rowResults: BulkResultRow[] = rawRows.map((entry, index) => {
    const record = entry as Record<string, unknown>;
    const status = String(record.status ?? (record.ok ? "ok" : "failed"));
    return {
      row: asNumber(record.row) ?? index + 1,
      email: typeof record.email === "string" ? record.email : "",
      status,
      ok: rowOk(status),
    };
  });

  const reportedImported = asNumber(result.imported_count);
  const reportedFailed = asNumber(result.failed_count);
  const failedFromRows = rowResults.filter((row) => !row.ok).length;

  // Failure detection priority: explicit failed_count > any failed row > ok flag.
  const failedCount = reportedFailed ?? (rowResults.length > 0 ? failedFromRows : result.ok ? 0 : 0);
  const validationFailed = result.ok === false || result.status === "partial_failure" || failedCount > 0;

  // Imported count: explicit field, else ready rows from the per-row results,
  // else (LIVE success) the count of rows we submitted.
  const importedFromRows = rowResults.filter((row) => row.ok).length;
  const importedCount = validationFailed
    ? reportedImported ?? importedFromRows
    : reportedImported ?? (rowResults.length > 0 ? importedFromRows : submittedReadyCount);

  const succeeded = !validationFailed && importedCount > 0;
  const partial = importedCount > 0 && failedCount > 0;

  let summary: string;
  if (partial) {
    summary = `Imported ${importedCount}, ${failedCount} row${failedCount === 1 ? "" : "s"} failed.`;
  } else if (validationFailed) {
    summary = `No participants were imported — ${failedCount || rowResults.length || "some"} row${
      failedCount === 1 ? "" : "s"
    } need fixing.`;
  } else if (succeeded) {
    summary = `Imported ${importedCount} participant${importedCount === 1 ? "" : "s"}.`;
  } else {
    summary = "No participants were imported.";
  }

  return {
    succeeded,
    partial,
    validationFailed,
    importedCount,
    failedCount,
    rowResults,
    summary,
  };
}
